<div class="checkbox-question">
  @if (isEditing()) {
    <div class="question-config">
      <label class="config-label">
        @if (getQuestionTypeIcon(); as iconInfo) {
          <span class="question-type-icon-wrapper">
            @if (iconInfo.iconType === 'prime') {
              <i [class]="'pi ' + iconInfo.icon" class="question-type-icon"></i>
            }
            @if (iconInfo.iconType === 'material') {
              <span class="material-symbols-outlined question-type-icon">{{ iconInfo.icon }}</span>
            }
          </span>
        }
        Question Text
      </label>
      <input
        #labelInput
        type="text"
        class="config-input label-input"
        [value]="question().labelText"
        (input)="onLabelChange($any($event.target).value)"
        [disabled]="disabled()"
        placeholder="Enter your question label here...">

      <label class="config-label">Additional Info</label>
      <input
        type="text"
        class="config-input"
        [value]="question().additionalInfo ?? ''"
        (input)="onAdditionalInfoChange($any($event.target).value)"
        [disabled]="disabled()"
        placeholder="Enter additional information">

      <label class="config-label">Helper Text</label>
      <input
        type="text"
        class="config-input"
        [value]="question().helperText ?? ''"
        (input)="onHelperTextChange($any($event.target).value)"
        [disabled]="disabled()"
        placeholder="Enter helper text">

      <label class="checkbox-label dynamic-options-toggle">
        <input
          type="checkbox"
          class="config-checkbox"
          [checked]="question().hasDynamicOptions ?? false"
          (change)="onDynamicOptionsToggle($any($event.target).checked)"
          [disabled]="disabled()">
        <span>Dynamic Options</span>
        <span class="material-symbols-outlined dynamic-icon">dynamic_feed</span>
      </label>

      <!-- Static Options Configuration -->
      @if (!question().hasDynamicOptions) {
        <label class="config-label">Options</label>
        <div class="options-list">
          @for (option of options; track $index; let i = $index) {
            <div class="option-item">
              <input
                #optionInput
                type="text"
                class="config-input option-input"
                [value]="option.text"
                (input)="onOptionChange(i, $any($event.target).value)"
                (blur)="onOptionBlur()"
                [disabled]="disabled()"
                [placeholder]="'Option ' + (i + 1)">
              <label class="custom-option-checkbox-label">
                <input
                  type="checkbox"
                  class="config-checkbox"
                  [checked]="option.isCustomOption"
                  (change)="onCustomOptionToggle(i, $any($event.target).checked)"
                  [disabled]="disabled()">
                <span>Custom</span>
              </label>
              <button
                type="button"
                class="remove-option-button"
                (click)="removeOption(i)"
                [disabled]="disabled() || options.length === 1"
                aria-label="Remove option">
                <span class="material-symbols-outlined">delete_forever</span>
              </button>
            </div>
          }
          <button
            type="button"
            class="add-option-button"
            (click)="addOption()"
            [disabled]="disabled()"
            aria-label="Add option">
            <i class="pi pi-plus"></i>
            <span>Add Option</span>
          </button>
        </div>
      }

      <!-- Dynamic Options Configuration -->
      @if (question().hasDynamicOptions) {
        <div class="dynamic-options-section">
          <label class="section-header">
            <span class="material-symbols-outlined section-icon">dynamic_feed</span>
            Option Generation Configuration
          </label>

          <div class="config-field">
            <label class="config-label">Source Container</label>
            <input
              type="text"
              class="config-input"
              [value]="question().sourceContainer ?? ''"
              (input)="onSourceContainerChange($any($event.target).value)"
              [disabled]="disabled()"
              placeholder="Enter source container path...">
          </div>

          <div class="config-field">
            <label class="config-label">Text Property</label>
            <input
              type="text"
              class="config-input"
              [value]="question().textProperty ?? ''"
              (input)="onTextPropertyChange($any($event.target).value)"
              [disabled]="disabled()"
              placeholder="Enter text property path...">
          </div>

          <div class="config-field">
            <label class="config-label">Custom Option ID</label>
            <input
              type="text"
              class="config-input"
              [value]="question().customOptionId ?? ''"
              (input)="onCustomOptionIdChange($any($event.target).value)"
              [disabled]="disabled()"
              placeholder="Enter custom option ID field...">
          </div>

          <div class="config-field">
            <label class="config-label">Custom Option Text</label>
            <input
              type="text"
              class="config-input"
              [value]="question().customOptionText ?? ''"
              (input)="onCustomOptionTextChange($any($event.target).value)"
              [disabled]="disabled()"
              placeholder="Enter custom option text field...">
          </div>
        </div>
      }

      <div class="required-toggle-row">
        <label class="config-label">
          <span class="required-asterisk">*</span>
          Required
        </label>
        <label class="required-toggle-switch">
          <input
            type="checkbox"
            [checked]="question().isRequired"
            (change)="onRequiredToggle()"
            [disabled]="disabled()">
          <span class="toggle-slider"></span>
        </label>
      </div>
    </div>
  } @else {
    <div class="question-preview">
      <div class="question-label-wrapper">
        <label class="question-label">
          <span [innerHTML]="sanitizeHtml(question().labelText || 'Checkbox')"></span>
          @if (question().isRequired) {
            <span class="required-indicator">*</span>
          }
        </label>
        @if (question().additionalInfo) {
          <span class="additional-info" [innerHTML]="sanitizeHtml(question().additionalInfo)"></span>
        }
      </div>
      <div class="checkbox-options-wrapper">
        <!-- Show actual checkboxes if options are populated (even for dynamic checkboxes in view mode) -->
        @if (hasPopulatedOptions()) {
          @for (option of getOptionsForDisplay(); track $index; let i = $index) {
            <div class="checkbox-option">
              <input
                type="checkbox"
                [id]="'checkbox-' + question().id + '-' + i"
                [value]="getOptionText(option)"
                [checked]="isOptionSelected(i)"
                (change)="onCheckboxToggle(i, $any($event.target).checked)"
                [disabled]="disabled()">
              <label [for]="'checkbox-' + question().id + '-' + i">
                {{ getOptionText(option) || 'Option ' + (i + 1) }}
              </label>
            </div>
          }
          <!-- The self-describe boxes sit under the options, as they do on the applicant form. -->
          @for (i of selectedCustomOptionIndices(); track i) {
            <div class="custom-option-wrapper">
              <input
                type="text"
                class="custom-option-input"
                [value]="options[i]?.customValue ?? ''"
                (input)="onCustomValueChange(i, $any($event.target).value)"
                [disabled]="disabled()"
                placeholder="Enter your custom value...">
            </div>
          }
        }

        <!-- Show dynamic options placeholder only when no options are populated -->
        @if (!hasPopulatedOptions() && question().hasDynamicOptions) {
          <div class="dynamic-checkbox-preview">
            <div class="checkbox-placeholder">
              <input type="checkbox" disabled>
              <span>Option will be loaded dynamically</span>
            </div>
          </div>
          <div class="dynamic-config-summary">
            <span class="material-symbols-outlined config-icon">dynamic_feed</span>
            <span class="config-text">Dynamic options configured</span>
          </div>
        }

        <!-- No options message for static checkboxes without options -->
        @if (!hasPopulatedOptions() && !question().hasDynamicOptions) {
          <p class="no-options-message">No options available</p>
        }

        @if (question().helperText) {
          <span class="helper-text">{{ question().helperText }}</span>
        }
      </div>
    </div>
  }
</div>

